import { Button, Modal } from '@/components/basic';
import CreateOrEditExternalIDs from '@/components/integrations/external-ids/CreateOrEditExternalIDs';
import DeleteExternalId from '@/components/integrations/external-ids/DeleteExternalId';
import { IntegrationsExternalIdsTable } from '@/components/integrations/external-ids/IntegrationsExternalIdsTable';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import { useExternalsQuery } from '@/hooks/useExternalsQuery';
import { useToggle } from '@/hooks/useToggle';
import { External, IHour, IResource, Office } from '@gettactic/api';
import { ScrollArea } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import Image from 'next/image';
import { useCallback, useState } from 'react';
import { IoAdd as Plus } from 'react-icons/io5';

type Props = {
  office: Office;
  resources?: IResource[];
  title: string;
};

export type IHourWithResName = IHour & {
  resName?: string;
};

export function groupSharedKey(external: External) {
  return `${external.id}_${external.object_type}_${external.object_id}_${external.external_id}_${
    external.name
  }`;
}

export type ConfirmOperationModal = {
  operation: 'delete' | 'edit';
  applyTo: External[];
};

export default function IntegrationsExternalIdsList({
  title,
  office,
  resources
}: Props) {
  const queryClient = useQueryClient();
  const externals = useExternalsQuery(resources?.[0].id || '', 'resources');
  const invalidateQuery = useCallback(
    () => queryClient.invalidateQueries(['useExternalsQuery']),
    [office]
  );

  const [operationModal, setOperationModal] =
    useState<ConfirmOperationModal | null>(null);
  const [isCreateOpen, toggleCreateOpen] = useToggle(false);

  return (
    <div className="col-span-12">
      <div className="mt-4 flex items-center justify-between border-gray-200 border-b pb-2">
        <div className={'flex items-center justify-center'}>
          <h3 className="font-medium text-2xl text-gray-900 leading-6">
            {title}
          </h3>
        </div>
        <div>
          <Button
            type="button"
            variant="primary"
            size="base"
            layout="squared"
            className="ml-4 inline-flex"
            onClick={() => {
              toggleCreateOpen();
              setOperationModal(null);
            }}
            iconLeft={Plus}
          >
            <span className="mx-2">Add External ID</span>
          </Button>
        </div>
      </div>

      <Modal
        title={resources ? 'New external ID' : ''}
        opened={isCreateOpen}
        onClose={() => toggleCreateOpen()}
      >
        <CreateOrEditExternalIDs
          key={isCreateOpen ? 'new' : 'new-close'}
          onClose={() => {
            toggleCreateOpen();
            setOperationModal(null);
          }}
          resources={resources}
          invalidateQuery={invalidateQuery}
        />
      </Modal>
      <Modal
        title="Delete Item"
        opened={
          operationModal
            ? operationModal.applyTo.length > 0 &&
              operationModal.operation === 'delete'
            : false
        }
        onClose={() => setOperationModal(null)}
        size="lg"
      >
        {operationModal ? (
          <DeleteExternalId
            invalidateQuery={invalidateQuery}
            externals={operationModal.applyTo}
            onClose={() => setOperationModal(null)}
          />
        ) : null}
      </Modal>
      {!externals.isLoading && (externals?.data || []).length === 0 ? (
        <div className="flex flex-col flex-nowrap overflow-hidden">
          <ScrollArea type="auto" offsetScrollbars className="h-full">
            <div className=" flex flex-col items-center justify-center">
              <div className="flex max-w-prose flex-col items-center">
                <Image
                  src="https://cdn.gettactic.com/website/images/custom-hours-empty.png"
                  width={313}
                  height={215}
                  alt="No external ID's"
                />
                <h3 className="max-w-prose place-self-start text-center font-bold text-gray-800 text-lg">
                  Get started clicking on the &ldquo;Add External ID's&ldquo;
                  button
                </h3>
              </div>
            </div>
          </ScrollArea>
        </div>
      ) : (
        <IntegrationsExternalIdsTable
          setOperationalModal={(x) => {
            if (x && x.operation === 'edit') {
              toggleCreateOpen();
            }
            setOperationModal(x);
          }}
          loading={false}
          data={externals.data || []}
          invalidateQuery={invalidateQuery}
        />
      )}

      {externals.isLoading ? <LoadingSpinner /> : null}
    </div>
  );
}
